import { ReactNode, useRef, useState } from 'react';
import { ModalsKeys, Nullable } from 'global';
import { grey } from '@mui/material/colors';
import { useNotifications } from 'modules/notification';
import { useParams, useHistory } from 'react-router-dom';
import { Button, capitalize, Stack } from '@mui/material';
import { ListingForm } from 'components/shared/TableListing/types';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { CardTitle } from 'components/shared/CardContainer/CardTitle';
import { useQuote, collectEquipmentQuoteVariables } from 'modules/quote';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { Charge, EquipmentDelivery, EquipmentFormValue } from 'modules/equipment/types';
import {
  Section,
  EquipmentForm,
  ChargesListing,
  CreateChargesDialog,
  useEquipmentActions,
  EquipmentDeliveryForm,
  ChargesListingLoading,
  EquipmentChargesDialog,
  CreateUpdateEquipmentDialog,
  prepareCreateUpdateEquipmentDTO,
  UploadImagesForNewEquipmentDialog,
  UploadImagesForExistingEquipmentDialog,
  PreviewExistingImagesDialog,
  EquipmentImageActions,
  useEquipmentAssets,
} from 'modules/equipment';
import { EquipmentPageSendEquipmentFlyer } from './EquipmentPageSendEquipmentFlyer';

const modals: Partial<Record<ModalsKeys, ReactNode>> = {
  [ModalsKeys.ChargesEquipmentDialog]: <EquipmentChargesDialog />,
  [ModalsKeys.CreateChargesEquipmentDialog]: <CreateChargesDialog />,
  [ModalsKeys.CreateUpdateEquipmentDialog]: <CreateUpdateEquipmentDialog />,
  [ModalsKeys.UploadImagesForNewEquipmentDialog]: <UploadImagesForNewEquipmentDialog />,
  [ModalsKeys.UploadImagesForExistingEquipmentDialog]: <UploadImagesForExistingEquipmentDialog />,
  [ModalsKeys.PreviewExistingEquipmentImagesDialog]: <PreviewExistingImagesDialog />,
};

export const EquipmentPage = () => {
  const [selectedImages, setSelectedImages] = useState<File[]>([]);

  const history = useHistory();
  const { showNotification } = useNotifications();

  const { VOId, dynamicVOId, equipmentId } = useParams<{
    VOId: string | undefined;
    dynamicVOId: string | undefined;
    equipmentId: string | undefined;
  }>();

  const { quote, isQuoteLoading, handleCreateOrUpdateQuote, isQuoteCreatingOrUpdating, error } = useQuote(VOId);
  const { onCreateEquipment, onUpdateEquipment, isAnyActionInProgress, onDeleteEquipment, isEquipmentDeleting } =
    useEquipmentActions();
  const { handleUploadImages } = useEquipmentAssets();

  const chargesSubmitRef = useRef<{ submit: () => ListingForm<Charge> } | null>(null);
  const equipmentSubmitRef = useRef<{ submit: () => EquipmentFormValue } | null>(null);
  const equipmentDeliverySubmitRef = useRef<
    Nullable<{
      submit: () => Promise<{ isValid: boolean; formValues: EquipmentDelivery }>;
    }>
  >(null);

  const handleGoToVO = () => {
    if (VOId) {
      history.push(`/vo/${VOId}`);
    } else {
      history.push('/inventory');
    }
  };

  const handleSubmitAll = async () => {
    const valuesEquipment = equipmentSubmitRef.current?.submit();
    const equipmentDeliveryResult = await equipmentDeliverySubmitRef.current?.submit();
    const valuesCharges = chargesSubmitRef.current?.submit();

    if (VOId && [equipmentDeliveryResult?.isValid].some(v => !Boolean(v))) {
      showNotification('Validation error', { type: 'error' });
      return;
    }

    const createUpdateDTO = {
      vendorOpportunityId: VOId ?? null,
      dynamicsVendorOpportunityId: dynamicVOId ?? null,
      ...prepareCreateUpdateEquipmentDTO(valuesEquipment!),
      ...equipmentDeliveryResult?.formValues,
    };

    if (!equipmentId && VOId === undefined) {
      const newEquipmentId = await onCreateEquipment(createUpdateDTO);

      // Upload images if any were selected
      if (selectedImages.length > 0 && newEquipmentId) {
        await handleUploadImages(newEquipmentId, 'IMAGE', selectedImages);
      }

      history.push('/inventory');
    } else if (!equipmentId && VOId !== 'new' && createUpdateDTO.dynamicsVendorOpportunityId) {
      const newEquipmentId = await onCreateEquipment(createUpdateDTO);

      // Upload images if any were selected
      if (selectedImages.length > 0 && newEquipmentId) {
        await handleUploadImages(newEquipmentId, 'IMAGE', selectedImages);
      }
      const quoteVariables = collectEquipmentQuoteVariables(
        { equipmentId: newEquipmentId, ...createUpdateDTO },
        quote,
        valuesCharges?.bodyFields || []
      );
      if (VOId) {
        await handleCreateOrUpdateQuote(VOId, quoteVariables);
      }
    } else if (equipmentId && createUpdateDTO.dynamicsVendorOpportunityId) {
      // @ts-expect-error dynamicsVendorOpportunityId cannot be null here
      await onUpdateEquipment({ equipmentId, ...createUpdateDTO });
      const quoteVariables = collectEquipmentQuoteVariables(
        { equipmentId, ...createUpdateDTO },
        quote,
        valuesCharges?.bodyFields || []
      );

      if (VOId) {
        await handleCreateOrUpdateQuote(VOId, quoteVariables);
      }
    } else if (equipmentId) {
      // @ts-expect-error dynamicsVendorOpportunityId cannot be null here
      await onUpdateEquipment({ equipmentId, ...createUpdateDTO });
      const quoteVariables = collectEquipmentQuoteVariables(
        { equipmentId, ...createUpdateDTO },
        quote,
        valuesCharges?.bodyFields || []
      );

      if (VOId) {
        await handleCreateOrUpdateQuote(VOId, quoteVariables);
      }
    }
  };

  return (
    <PageContainer modals={modals} title="Equipment" error={error}>
      <CardContainer
        title={
          <CardTitle
            label={`${capitalize(equipmentId ? 'edit' : 'add')} Equipment`}
            onRedirect={VOId ? handleGoToVO : () => history.push('/inventory')}
          />
        }
      >
        <Stack gap="2rem">
          <Section
            title="Equipment"
            sx={{ borderRadius: '0.5rem', border: `1px solid ${grey[300]}` }}
            action={<EquipmentImageActions equipmentId={equipmentId} onImagesSaved={setSelectedImages} />}
          >
            <EquipmentForm equipmentId={equipmentId} submitRef={equipmentSubmitRef} />
          </Section>

          {VOId && (
            <Section title="Delivery Address">
              <EquipmentDeliveryForm equipmentId={equipmentId} submitRef={equipmentDeliverySubmitRef} />
            </Section>
          )}

          {VOId && (
            <Section isLoading={isQuoteLoading} fallBack={<ChargesListingLoading />}>
              <ChargesListing quote={quote} equipmentId={equipmentId} submitRef={chargesSubmitRef} />
            </Section>
          )}

          <Stack direction="row" justifyContent="flex-end" gap="1rem">
            <Button disabled={isQuoteCreatingOrUpdating || isAnyActionInProgress} onClick={handleGoToVO}>
              Cancel
            </Button>

            {equipmentId && VOId && (
              <Button
                color="error"
                loading={isEquipmentDeleting}
                onClick={() => {
                  onDeleteEquipment(
                    {
                      equipmentId,
                      vendorOpportunityId: VOId,
                      dynamicsVendorOpportunityId: dynamicVOId,
                    },
                    handleGoToVO
                  );
                }}
              >
                Remove
              </Button>
            )}

            <Button
              variant="contained"
              onClick={handleSubmitAll}
              loading={isQuoteCreatingOrUpdating || isAnyActionInProgress}
            >
              Save
            </Button>
          </Stack>
        </Stack>

        {equipmentId && <EquipmentPageSendEquipmentFlyer equipmentId={equipmentId} />}
      </CardContainer>
    </PageContainer>
  );
};
